// NodeDateTimeModal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './NodeDateTimeModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import NodeDateTimeInput from 'node/form/fields/datetime/NodeDateTimeInput';

/**
 * NodeDateTimeModal
 *
 * Modal editor for schema-driven datetime fields.
 *
 * @param {object} value - Current field value.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onChange - Callback when the value is saved.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateTimeModal = ({ value, isOpen, setOpen, onChange }) => {

  // ----- STATE ----- //

  // Local datetime state for editing within the modal

  const [dateTime, setDateTime] = useState(null);

  // ----- REFS ----- //

  // Ref to hold the latest datetime value for saving

  const dateTimeRef = useRef(null);

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- EFFECTS ----- //

  // When modal opens, initialize local state with current value

  useEffect(() => {
    if (!isOpen) return;
    const nextValue = value ? { ...value } : null;
    dateTimeRef.current = nextValue;
    setDateTime(nextValue);
  }, [isOpen, value]);

  // ----- HANDLERS ----- //

  /**
   * handleDateTimeChange
   *
   * Handles date time change.
   *
   * @param {string} nextValue - nextValue.
   */
  const handleDateTimeChange = (nextValue) => {
    dateTimeRef.current = nextValue;
    setDateTime(nextValue);
  };

  /**
   * Handles saving the datetime value. Blurs any active element to close virtual keyboards on mobile,
   * then calls the onChange callback with the latest datetime value and closes the modal.
   */
  const handleSave = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (typeof onChange === 'function') {
      onChange(dateTimeRef.current ?? dateTime);
    }
    setOpen(false);
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title="Date & Time"
      isOpen={isOpen}
      width={isMobile ? '100%' : 500}
      height={270}
      onClose={() => setOpen(false)}
    >
      <ModalForm>

        {/* WRAPPER */}
        <div className="node-date-time-modal-wrapper">
          <NodeDateTimeInput
            value={dateTime}
            hybrid={false}
            onChange={handleDateTimeChange}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>
    </Modal>
  );
};

// Dev Tools
NodeDateTimeModal.displayName = 'NodeDateTimeModal';

export default NodeDateTimeModal;
